import React, { useMemo, useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer } from "recharts";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Info, TrendingUp, Loader2, AlertCircle } from "lucide-react";
import { getCompetitivenessData } from "../services/competitivenessApi";

/**
 * Global Competitiveness Tracker: Comparative Self-Reliance (ASR %) for China, USA, India
 * - Uses Apparent Consumption = Production + Imports - Exports
 * - ASR % = Production / Apparent Consumption * 100 (clipped to [0, 120])
 *
 * Edit the `rawRows` array with your real data (tonnes, contained metal).
 */

// Custom Tooltip Component - defined outside to prevent re-creation on render
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
        <p className="font-semibold text-gray-900 dark:text-white mb-2">{label}</p>
        {payload.map((entry, index) => {
          const value = entry.value;
          const displayValue = typeof value === 'number' && !isNaN(value) ? value.toFixed(1) : value || '0';
          return (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {displayValue}%
            </p>
          );
        })}
      </div>
    );
  }
  return null;
};

function asrPct(row) {
  const cons = row.p + row.i - row.e;
  if (!cons || cons <= 0) return null;
  return Math.max(0, Math.min(120, (row.p / cons) * 100));
}

function groupByMineral(rows, countries) {
  const minerals = Array.from(new Set(rows.map(r => r.mineral)));
  return minerals.map(mineral => {
    const entry = { mineral };
    countries.forEach(c => {
      const r = rows.find(x => x.mineral === mineral && x.country === c);
      entry[c] = r ? asrPct(r) : null;
    });
    return entry;
  });
}

function normalizeCountryName(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return '';
  if (raw === 'united states' || raw === 'u.s.' || raw === 'us') return 'USA';
  if (raw.includes('india')) return 'India';
  if (raw.includes('china')) return 'China';
  return String(value || '').trim();
}

function kpiSummaries(rows) {
  const latestYear = Math.max(...rows.map(r => r.year));
  const byCountry = new Map();
  rows.filter(r => r.year === latestYear).forEach(r => {
    const val = asrPct(r);
    if (val == null) return;
    if (!byCountry.has(r.country)) byCountry.set(r.country, []);
    byCountry.get(r.country).push(val);
  });
  const avg = Array.from(byCountry.entries()).map(([country, vals]) => ({
    country,
    avgAsr: vals.length ? vals.reduce((a,b)=>a+b,0) / vals.length : null,
  })).filter(x => x.avgAsr != null).sort((a,b)=>b.avgAsr - a.avgAsr);

  const most = avg[0];
  const least = avg[avg.length - 1];
  
  // Find top mineral by average ASR
  const byMineral = new Map();
  rows.filter(r => r.year === latestYear).forEach(r => {
    const val = asrPct(r);
    if (val == null) return;
    if (!byMineral.has(r.mineral)) byMineral.set(r.mineral, []);
    byMineral.get(r.mineral).push(val);
  });
  const mineralAvg = Array.from(byMineral.entries()).map(([mineral, vals]) => ({
    mineral,
    avgAsr: vals.length ? vals.reduce((a,b)=>a+b,0) / vals.length : null,
  })).filter(x => x.avgAsr != null).sort((a,b)=>b.avgAsr - a.avgAsr);
  
  const topMineral = mineralAvg[0];
  const leastMineral = mineralAvg[mineralAvg.length - 1];
  
  return { latestYear, most, least, topMineral, leastMineral };
}

const GlobalCompetitiveness = () => {
  const [countries] = useState(["China", "USA", "India"]);
  const [stacked, setStacked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiData, setApiData] = useState(null);
  const [latestYear, setLatestYear] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch competitiveness data
        const response = await getCompetitivenessData({ countries });
        
        // Check if we have data
        if (response && response.success && response.data && response.data.length > 0) {
          setApiData(response.data);

          // Prefer latest year where all selected countries are present.
          const rows = response.data;
          const yearsDesc = Array.from(new Set(rows.map((d) => d.year))).sort((a, b) => b - a);
          const countriesSet = new Set(countries.map((c) => normalizeCountryName(c)));
          let chosenYear = yearsDesc[0] || null;
          for (const y of yearsDesc) {
            const present = new Set(
              rows
                .filter((d) => d.year === y)
                .map((d) => normalizeCountryName(d.country)),
            );
            const hasAllCountries = Array.from(countriesSet).every((c) => present.has(c));
            if (hasAllCountries) {
              chosenYear = y;
              break;
            }
          }

          setLatestYear(chosenYear);
          setSelectedYear(chosenYear);
        } else {
          // No data from API
          setApiData([]);
          setLatestYear(null);
          setSelectedYear(null);
        }
        
      } catch (err) {
        console.error('Error fetching competitiveness data:', err);
        setError('Unable to fetch data from server. Please ensure the backend is running and data is populated.');
        setApiData([]);
        setLatestYear(null);
        setSelectedYear(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [countries]);

  const availableYears = useMemo(() => {
    if (!apiData || apiData.length === 0) return [];
    return Array.from(new Set(apiData.map((d) => d.year)))
      .filter((y) => y != null)
      .sort((a, b) => b - a);
  }, [apiData]);

  useEffect(() => {
    if (!availableYears.length) return;
    if (selectedYear == null || !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  // Convert API data to the format expected by the UI
  const rows = useMemo(() => {
    if (!apiData || !selectedYear) {
      return [];
    }
    
    // Normalize and aggregate API rows by (country, mineral, year) first.
    // This avoids incorrect ASR values when the backend returns multiple rows
    // for the same logical country+mineral pair.
    const normalized = apiData
      .map(d => ({
        ...d,
        country: normalizeCountryName(d.country),
      }))
      .filter(d => d.year === selectedYear && countries.includes(d.country));

    const grouped = new Map();
    normalized.forEach((d) => {
      const key = `${d.country}__${d.mineral}__${d.year}`;
      const existing = grouped.get(key) || {
        country: d.country,
        mineral: d.mineral,
        year: d.year,
        p: 0,
        i: 0,
        e: 0,
      };
      existing.p += Number(d.production_t || 0);
      existing.i += Number(d.imports_t || 0);
      existing.e += Number(d.exports_t || 0);
      grouped.set(key, existing);
    });

    return Array.from(grouped.values());
  }, [apiData, selectedYear, countries]);

  const data = useMemo(() => groupByMineral(rows, countries), [rows, countries]);
  
  // Calculate KPI cards from the same filtered rows as chart/table.
  const { most, least, topMineral, leastMineral } = useMemo(() => {
    return kpiSummaries(rows);
  }, [rows]);

  // Loading state
  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white w-full min-h-screen transition-colors duration-200 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-lg text-gray-600 dark:text-gray-400">Loading competitiveness data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white w-full min-h-screen transition-colors duration-200">
      <div className="w-full px-4 md:px-8 py-8">
        <div className="space-y-6">
          {/* Error Banner */}
          {error && (
            <Card className="shadow-md rounded-2xl bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
              <CardContent className="p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </CardContent>
            </Card>
          )}

          {/* No Data Message */}
          {!error && (!apiData || apiData.length === 0) && (
            <Card className="shadow-md rounded-2xl bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
              <CardContent className="p-6 text-center">
                <Info className="w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
                  No Data Available
                </h3>
                <p className="text-sm text-blue-700 dark:text-blue-300 mb-4">
                  No mineral self-reliance data found in the database.
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  Run <code className="bg-blue-100 dark:bg-blue-800 px-2 py-1 rounded">python populate_mineral_self_reliance_data.py</code> to populate sample data.
                </p>
              </CardContent>
            </Card>
          )}
          
          {/* Header */}
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                Global Competitiveness Tracker
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                Critical Mineral Self-Reliance (ASR %) Analysis — {selectedYear ?? latestYear}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={selectedYear ?? ''}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="rounded-2xl px-4 py-2 border border-gray-300 bg-white text-gray-900 text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300"
              >
                {availableYears.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <Button 
                onClick={() => setStacked(s => !s)} 
                variant="outline"
                className="rounded-2xl px-6 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {stacked ? "Grouped View" : "Stacked View"}
              </Button>
            </div>
          </div>

          {/* KPI Cards - Only show if we have data */}
          {apiData && apiData.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="shadow-md rounded-2xl bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 transition-colors duration-200">
              <CardContent className="p-5">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Most Self-Reliant Country</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mt-2">{most?.country ?? "—"}</div>
                <div className="text-sm text-blue-600 dark:text-blue-400 mt-1 font-medium">
                  {most && most.avgAsr != null ? `${most.avgAsr.toFixed(1)}% ` : most && most.avg_asr != null ? `${most.avg_asr.toFixed(1)}%` : "—"}
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-md rounded-2xl bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 transition-colors duration-200">
              <CardContent className="p-5">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Least Self-Reliant Country</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mt-2">{least?.country ?? "—"}</div>
                <div className="text-sm text-red-600 dark:text-red-400 mt-1 font-medium">
                  {least && least.avgAsr != null ? `${least.avgAsr.toFixed(1)}%` : least && least.avg_asr != null ? `${least.avg_asr.toFixed(1)}%` : "—"}
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-md rounded-2xl bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 transition-colors duration-200">
              <CardContent className="p-5">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Top Self-Reliant Mineral</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mt-2">{topMineral?.mineral ?? "—"}</div>
                <div className="text-sm text-green-600 dark:text-green-400 mt-1 font-medium">
                  {topMineral && topMineral.avgAsr != null ? `${topMineral.avgAsr.toFixed(1)}%` : topMineral && topMineral.avg_asr != null ? `${topMineral.avg_asr.toFixed(1)}%` : "—"}
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-md rounded-2xl bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 transition-colors duration-200">
              <CardContent className="p-5">
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Most Import-Dependent</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white mt-2">{leastMineral?.mineral ?? "—"}</div>
                <div className="text-sm text-orange-600 dark:text-orange-400 mt-1 font-medium">
                  {leastMineral && leastMineral.avgAsr != null ? `${leastMineral.avgAsr.toFixed(1)}%` : leastMineral && leastMineral.avg_asr != null ? `${leastMineral.avg_asr.toFixed(1)}%` : "—"}
                </div>
              </CardContent>
            </Card>
            </div>
          )}

          {/* Formula Info Card - Only show if we have data */}
          {apiData && apiData.length > 0 && (
            <Card className="shadow-md rounded-2xl bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 transition-colors duration-200">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white mb-2">ASR Formula</div>
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    ASR % = Production / (Production + Imports − Exports) × 100
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                    Units: tonnes (contained metal). Values clipped to 120% to show net exporters.
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Chart - Only show if we have data */}
          {apiData && apiData.length > 0 && (
            <Card className="shadow-md rounded-2xl bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 transition-colors duration-200">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Comparative Self-Reliance by Mineral
              </h2>
              <div className="w-full h-[500px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data} margin={{ top: 20, right: 30, bottom: 60, left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" className="dark:stroke-gray-700" />
                    <XAxis 
                      dataKey="mineral" 
                      tick={{ fontSize: 13, fill: '#6b7280' }} 
                      angle={-15}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis 
                      domain={[0, 120]} 
                      tickFormatter={(v)=>`${v}%`}
                      tick={{ fontSize: 13, fill: '#6b7280' }}
                      label={{ value: 'ASR %', angle: -90, position: 'insideLeft', style: { fill: '#6b7280' } }}
                    />
                    <Tooltip 
                      content={<CustomTooltip />}
                      cursor={{ fill: 'rgba(0, 0, 0, 0.1)' }}
                      wrapperStyle={{ zIndex: 1000 }}
                      allowEscapeViewBox={{ x: true, y: true }}
                    />
                    <Legend 
                      verticalAlign="top" 
                      height={40}
                      wrapperStyle={{ paddingTop: '10px' }}
                      iconType="rect"
                    />
                    <Bar 
                      dataKey="China" 
                      stackId={stacked ? "a" : undefined} 
                      fill="#3b82f6" 
                      radius={[6,6,0,0]}
                      name="China"
                    />
                    <Bar 
                      dataKey="USA" 
                      stackId={stacked ? "a" : undefined} 
                      fill="#10b981" 
                      radius={[6,6,0,0]}
                      name="USA"
                    />
                    <Bar 
                      dataKey="India" 
                      stackId={stacked ? "a" : undefined} 
                      fill="#f59e0b" 
                      radius={[6,6,0,0]}
                      name="India"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Data Table - Only show if we have data */}
          {apiData && apiData.length > 0 && (
            <Card className="shadow-md rounded-2xl bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 transition-colors duration-200">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Detailed ASR % Breakdown
              </h2>
              <div className="overflow-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left p-4 font-semibold text-gray-900 dark:text-white">Mineral</th>
                      {countries.map(c => (
                        <th key={c} className="text-right p-4 font-semibold text-gray-900 dark:text-white">{c} ASR %</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row, idx) => (
                      <tr 
                        key={row.mineral} 
                        className={`border-b border-gray-200 dark:border-gray-700 ${
                          idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900'
                        }`}
                      >
                        <td className="p-4 font-medium text-gray-900 dark:text-white">{row.mineral}</td>
                        {countries.map(c => (
                          <td key={c} className="p-4 text-right text-gray-700 dark:text-gray-300">
                            {row[c] != null ? (
                              <span className={`font-medium ${
                                row[c] >= 80 ? 'text-green-600 dark:text-green-400' : 
                                row[c] >= 40 ? 'text-yellow-600 dark:text-yellow-400' : 
                                'text-red-600 dark:text-red-400'
                              }`}>
                                {row[c].toFixed(1)}%
                              </span>
                            ) : '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          )}

          {/* Data source */}
          {apiData && apiData.length > 0 && (
            <div className="text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                Data source
              </h3>
              <ul className="text-xs sm:text-sm list-disc list-inside space-y-1.5 mb-3 text-gray-600 dark:text-gray-300">
                <li>
                  <span className="font-medium text-gray-800 dark:text-gray-200">Imports</span>
                  {' — '}<span className="font-mono text-[11px] sm:text-xs">import_dependency_scores</span>
                  {' '}(country totals by mineral and year)
                </li>
                <li>
                  <span className="font-medium text-gray-800 dark:text-gray-200">Exports</span>
                  {' — '}<span className="font-mono text-[11px] sm:text-xs">export_dependency_scores</span>
                  {' '}(country totals by mineral and year)
                </li>
                <li>
                  <span className="font-medium text-gray-800 dark:text-gray-200">Production (global)</span>
                  {' — '}<span className="font-mono text-[11px] sm:text-xs">world_production_reserves_mcs</span>
                  {' '}(MCS-based mine production; 2023 actuals and 2024 estimates as stored)
                </li>
                <li>
                  <span className="font-medium text-gray-800 dark:text-gray-200">Production (India)</span>
                  {' — '}<span className="font-mono text-[11px] sm:text-xs">india_production_data_bgs</span>
                  {' '}(where available; preferred over MCS for India when joined)
                </li>
              </ul>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                <span className="font-medium text-gray-700 dark:text-gray-300">Units and methodology:</span>
                {' '}Quantities are in tonnes of contained metal. Apparent consumption = production + imports − exports.
                ASR % = production ÷ apparent consumption × 100, capped between 0% and 120%.
                If apparent consumption is zero or negative, ASR % is shown as not applicable (—).
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalCompetitiveness;

